import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'
import gsap from 'gsap'

// import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import { useLoaderStore } from '@/store/loader'
import { pageHasHero } from '@/utils/page'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type LoaderProps = HTMLAttributes<HTMLDivElement> & {
  pageProps: WPPageData
}

const Loader = ({ pageProps, ...props }: LoaderProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  const fontsLoaded = useLoaderStore((state) => state.fontsLoaded)
  const heroLoaded = useLoaderStore((state) => state.heroLoaded)
  const siteLoaded = useLoaderStore((state) => state.siteLoaded)
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)
  const TIMER = 2200
  const [minimumTimer, setMinimumTimer] = useState(false)

  useEffect(() => {
    gsap.to('[data-logo-loader]', {
      opacity: 1,
      duration: 0.5,
      delay: 0.4,
      ease: 'power2.inOut'
    })
    setTimeout(() => {
      setMinimumTimer(true)
    }, TIMER)
  }, [])

  useEffect(() => {
    document.fonts.ready.then(() => {
      setLoaderState({ fontsLoaded: true })
    })
  }, [setLoaderState])

  useEffect(() => {
    !hasHero &&
      !siteLoaded &&
      minimumTimer &&
      fontsLoaded &&
      gsap.to('[data-logo-loader]', {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          setLoaderState({ siteLoaded: true })
        }
      })
  }, [hasHero, setLoaderState, siteLoaded, minimumTimer, fontsLoaded])

  return (
    <Transition
      nodeRef={root}
      in={!siteLoaded && (!heroLoaded || !minimumTimer || !fontsLoaded)}
      onExited={() => setLoaderState({ siteLoaded: true })}
      timeout={{
        enter: 0,
        exit: 1000
      }}
      unmountOnExit
    >
      {(state) => (
        <div
          ref={root}
          data-loader
          className={cn(
            'fixed inset-0 z-loader',
            'bg-surface-primay-invert-dark',
            'flex items-center justify-center',
            'transition-opacity delay-500 duration-1000 ease-in-out-cubic',
            {
              'opacity-0 duration-0': state === 'entering',
              'opacity-100': state === 'entered',
              'pointer-events-none opacity-0':
                state === 'exiting' || state === 'exited'
            }
          )}
          {...props}
        >
          <div
            role="status"
            aria-live="polite"
            className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-spacing-sm text-text-primary-dark opacity-0"
            data-logo-loader
          >
            <SVGIcon
              symbol="logo"
              className="h-[16px] w-[116px] scale-[115%] fill-current"
              aria-hidden="true"
            />
            {/* <Spinner
              variant="medium"
              className="size-[1.6rem] [&_circle]:stroke-[.8rem]"
            /> */}
            <span className="sr-only">Loading</span>
          </div>
        </div>
      )}
    </Transition>
  )
}

export default Loader
